interface IAttempt {
    window: Window | HTMLIFrameElement;
    state: string;
    callback: (event: MessageEvent<ILoginSuccess | ILoginFailed>) => void;
    prom: [
        Promise<ILoginSuccess>,
        (v: ILoginSuccess) => void,
        (e: Error) => void,
        (() => void) | undefined,
    ];
}

let attempt: IAttempt | null = null;

interface ILoginSuccess {
    type: "login";
    access_token: string;
    token_type: "Bearer";
    refresh_token?: string;
    /** Expiration time in seconds. */
    expires_in?: number;
    id_token?: string;
    scope?: string;
    state?: string;
}

interface ILoginFailed {
    type: "error";
    error: string;
    error_description?: string;
    state?: string;
}

export function login(
    prompt: "select_account" | "login" | "none" | "consent" = "select_account",
    tenant?: string
): Promise<ILoginSuccess> {
    const state = Math.floor(Math.random() * 0xffffff).toString(16);
    const url = `${window.origin}/session/create?state=${encodeURIComponent(
        state
    )}&tenant=${
        tenant && tenant !== "_" ? encodeURIComponent(tenant) : ""
    }&prompt=${prompt}&scope=openid+profile`;
    let loginWindowTmp:
        | ReturnType<typeof window.open>
        | HTMLIFrameElement
        | null;
    if (prompt != "none") {
        loginWindowTmp = window.open(
            url,
            "mara-login",
            "width=550,menubar=0,toolbar=0,location=0,status=0"
        );
    } else {
        const ifr = document.createElement("iframe");
        ifr.hidden = true;
        ifr.classList.add("hide");
        Promise.resolve().then(() => {
            ifr.src = url;
            document.body.appendChild(ifr);
        });
        loginWindowTmp = ifr;
    }
    if (!loginWindowTmp) {
        throw new Error("Unable to open popup window.");
    }
    const loginWindow = loginWindowTmp;
    if (attempt) {
        window.removeEventListener("message", attempt.callback);
    }
    const prom: [
        Promise<ILoginSuccess>,
        (v: ILoginSuccess) => void,
        (e: Error) => void,
        (() => void) | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] = [] as any;
    prom[0] = new Promise((res, rej) => {
        prom[1] = (x) => {
            prom[3]?.();
            res(x);
        };
        prom[2] = (e) => {
            prom[3]?.();
            rej(e);
        };
    });
    if (loginWindow instanceof HTMLIFrameElement) {
        const h = setTimeout(() => {
            prom[3]?.();
            prom[2](new Error("Login timed out."));
        }, 10000);
        prom[3] = () => {
            clearTimeout(h);
            prom[3] = undefined;
        };
    } else {
        const h = setInterval(() => {
            if (loginWindow.closed) {
                prom[3]?.();
                prom[2](new Error("Login window closed."));
            }
        }, 120);
        prom[3] = () => {
            clearInterval(h);
            prom[3] = undefined;
        };
        Promise.resolve().then(() => loginWindow.focus());
    }
    const att: Partial<IAttempt> = {
        window: loginWindow,
        state,
        prom,
    };
    const f = loginMessage.bind(att as IAttempt);
    att.callback = f;
    window.addEventListener("message", f);
    attempt = att as IAttempt;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return attempt.prom[0];
}

function loginMessage(
    this: IAttempt,
    event: MessageEvent<ILoginSuccess | ILoginFailed>
) {
    if (attempt !== this) {
        console.warn("Unexpected login message source.");
        return;
    }
    if (event.origin !== window.location.origin) {
        console.warn("Unexpected login message origin.");
        return;
    }
    const data = event.data;
    if (
        typeof data != "object" ||
        !data ||
        (!data.state && data.type !== "error")
    ) {
        console.warn("Unexpected login message data.");
        return;
    }
    if (data.type != "login" && data.type != "error") {
        console.warn("Unexpected login message type.");
        return;
    }
    if (data.state !== this.state && data.type !== "error") {
        console.warn("Unexpected login message state.");
        return;
    }
    window.removeEventListener("message", this.callback);
    if (data.type === "error") {
        console.error("Login failed:", data);
        const error = Object.assign(new Error(data.error), {
            error_description: data.error_description ?? data.error,
        });
        this.prom[2](error);
    } else {
        this.prom[1](data);
    }
    if (typeof (attempt.window as Window).close === "function") {
        try {
            (attempt.window as Window).close();
        } catch (e) {
            console.error(e);
        }
    } else {
        (attempt.window as HTMLIFrameElement).remove();
    }
}

export async function backgroundLogin(tenant?: string): Promise<null | string> {
    const loginopts = await login("none", tenant);
    return loginopts?.access_token;
}

export async function selectAccountLogin(tenant?: string): Promise<string> {
    const loginopts = await login("select_account", tenant);
    return loginopts.access_token;
}
